import React from 'react';
import styled from '@emotion/styled';
import ContentEditable from 'react-contenteditable';
import { breakpoints } from 'gatsby-theme-apollo';

const StyledContentEditable = styled(ContentEditable)(props => ({
  fontSize: '16px',
  width: '100%',
  maxWidth: '100px',
  textAlign: 'center',
  border: props.disabled ? 'none' : '1px solid rgba(0,0,0,.1)',
  backgroundColor: props.disabled ? 'transparent' : 'rgba(0,0,0,.08)',
  borderRadius: '4px',
  padding: '2px',
}));

const Input = styled.input(props => ({
  display: `flex`,
  alignItems: `center`,
  justifyContent: `center`,
  padding: `5px 5px`,
  boxSizing: `border-box`,
  minWidth: props.minWidth || 'inherit',
  textAlign: props.textAlign ? props.textAlign : 'auto',
  textIndent: props.textIndent || '3px',
  boxShadow: 'none',
  flex: `1`,
  width: props.width || `100%`,
  maxWidth: props.maxWidth || 'inherit',
  borderRadius: `5px`,
  border: props.disabled ? 'none' : '1px solid rgba(0,0,0,.6)',
  color: props.dark ? `white` : `black`,
  backgroundColor: props.disabled
    ? 'rgba(0,0,0,.08)'
    : props.dark
    ? `rgba(255,255,255,.1)`
    : `white`,
  [breakpoints.md]: {
    width: '100%'
  }
}));

const TextArea = Input.withComponent('textarea');

const keyCodes = {
  ENTER: 13,
  BACKSPACE: 8,
  DELETE: 46
};

const EditableText = ({
  inputType,
  textAlign,
  rows,
  placeholder,
  textIndent,
  editingDisabled,
  onInputChanged,
  onBlur,
  onFocus,
  onClicked,
  onKeyPress,
  dark,
  textValue,
  maxCharacters
}) => {
  const contentEditableRef = React.useRef(null);
  const inputRef = React.useRef(null);

  const updateText = e => {
    let newValue = 'unknown';
    if (inputType === 'content-editable' && contentEditableRef.current) {
      newValue = contentEditableRef.current.innerHTML;
    } else {
      newValue = e.target.value;
    }

    if (onInputChanged) {
      onInputChanged(newValue);
    }
  };

  let InputElement;

  switch (inputType) {
    case 'textarea':
      InputElement = (
        <TextArea
          value={textValue}
          rows={rows || 1}
          onBlur={e => {
            if (onBlur) {
              onBlur(e.target.value);
            }
          }}
          onChange={e => {
            if (onInputChanged) {
              onInputChanged(e.target.value);
            }
          }}
          onFocus={() => {
            if (onFocus) {
              onFocus();
            }
          }}
        />
      );
      break;
    case 'content-editable':
      InputElement = (
        <StyledContentEditable
          ref={contentEditableRef}
          html={textValue}
          tagName="span"
          data-skip-resize
          onKeyDown={e => {
            if (onKeyPress) {
              onKeyPress(e.keyCode);
            }
            if (e.keyCode === keyCodes.ENTER) {
              e.target.blur();
            }
          }}
          disabled={editingDisabled}
          onFocus={e => {
            if (onFocus) {
              onFocus(e);
            }
          }}
          onBlur={e => {
            updateText(e);
            if (onBlur) {
              onBlur(e);
            }
          }}
          onChange={e => {
            if (onInputChanged) {
              onInputChanged(e.target.value);
            }
          }}
          onClick={() => {
            if (onClicked) {
              onClicked();
            }
          }}
        />
      );
      break;
    default:
      InputElement = (
        <Input
          disabled={editingDisabled}
          dark={dark}
          placeholder={placeholder}
          maxLength={maxCharacters}
          textIndent={textIndent}
          textAlign={textAlign}
          autoCapitalize="off"
          autocomplete="off"
          value={textValue}
          ref={inputRef}
          onBlur={e => {
            if (onBlur) {
              onBlur(e.target.value);
            }
          }}
          onFocus={() => {
            if (onFocus) {
              onFocus();
            }
          }}
          onChange={e => {
            if (onInputChanged) {
              onInputChanged(e.target.value);
            }
          }}
          onKeyDown={e => {
            if (onKeyPress) {
              onKeyPress(e.keyCode);
            }
            if (e.keyCode === keyCodes.ENTER) {
              e.target.blur();
            }
          }}
        />
      );
  }

  return <>{InputElement}</>;
};

export default EditableText;